import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

import type { ResumeTemplateId } from '../../types/resumeTemplate'
import {
  buildColumnSliceState,
  sliceHeightForPage,
} from '../../utils/resumePreviewBreakSnap'
import {
  computeResumePreviewPageMetrics,
  RESUME_PREVIEW_PAGE,
} from '../../utils/resumePreviewPageLayout'
import ResumePreviewTwoColumnPageGuides from './ResumePreviewTwoColumnPageGuides'
import ResumePreviewPanelFit from './ResumePreviewPanelFit'
import ResumePreviewDebugContent from './ResumePreviewDebugContent'
import ResumePreviewDebugLegend from './ResumePreviewDebugLegend'
import { ResumePreviewFontSizeLabelsOverlay } from './ResumePreviewFontSizeLabelsContent'
import { ResumePreviewPageBreakEdge } from './ResumePreviewLineGuidesOverlay'

type ResumePreviewPageGuidesProps = {
  children: React.ReactNode
  template: ResumeTemplateId
  lineGuides?: boolean
  fontSizeDebug?: boolean
}

/**
 * Stacked A4 sheets with PDF margins; breaks snap to line/block boundaries (no mid-line cuts).
 */
export default function ResumePreviewPageGuides({
  children,
  template,
  lineGuides = false,
  fontSizeDebug = false,
}: ResumePreviewPageGuidesProps) {
  if (template === 'two_column') {
    return (
      <ResumePreviewTwoColumnPageGuides lineGuides={lineGuides} fontSizeDebug={fontSizeDebug}>
        {children}
      </ResumePreviewTwoColumnPageGuides>
    )
  }
  return (
    <ResumePreviewClassicPageGuides lineGuides={lineGuides} fontSizeDebug={fontSizeDebug}>
      {children}
    </ResumePreviewClassicPageGuides>
  )
}

function ResumePreviewClassicPageGuides({
  children,
  lineGuides = false,
  fontSizeDebug = false,
}: {
  children: React.ReactNode
  lineGuides?: boolean
  fontSizeDebug?: boolean
}) {
  const measureRef = useRef<HTMLDivElement>(null)
  const [metrics, setMetrics] = useState(() => computeResumePreviewPageMetrics())
  const [sliceStarts, setSliceStarts] = useState<number[]>([0])
  const [sliceLeads, setSliceLeads] = useState<number[]>([0])
  const [bodyHeight, setBodyHeight] = useState(0)

  const pageFrameStyle = useMemo(
    () => ({
      boxSizing: 'border-box' as const,
      width: '100%',
      height: '100%',
      paddingTop: `${RESUME_PREVIEW_PAGE.marginTopMm}mm`,
      paddingRight: `${RESUME_PREVIEW_PAGE.marginRightMm}mm`,
      paddingBottom: `${RESUME_PREVIEW_PAGE.marginBottomMm}mm`,
      paddingLeft: `${RESUME_PREVIEW_PAGE.marginLeftMm}mm`,
    }),
    []
  )

  const remeasure = useCallback(() => {
    const next = computeResumePreviewPageMetrics()
    setMetrics(next)
    const root = measureRef.current
    if (!root) return
    const colState = buildColumnSliceState(root, next.contentZoneHeightPx)
    setBodyHeight(colState.totalHeight)
    setSliceStarts(colState.starts)
    setSliceLeads(colState.leads)
  }, [])

  useLayoutEffect(() => {
    remeasure()
  }, [children, remeasure])

  useLayoutEffect(() => {
    const el = measureRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => remeasure())
    ro.observe(el)
    return () => ro.disconnect()
  }, [remeasure])

  const { pageWidthPx, pageHeightPx, contentWidthPx, contentZoneHeightPx } = metrics
  const pageCount = sliceStarts.length

  return (
    <>
      <div className="resume-preview-measure" aria-hidden>
        <div
          ref={measureRef}
          className="resume-preview-measure-inner"
          style={{
            width: contentWidthPx > 0 ? contentWidthPx : `${RESUME_PREVIEW_PAGE.pageWidthMm}mm`,
          }}
        >
          {children}
        </div>
      </div>

      <ResumePreviewPanelFit pageWidthPx={pageWidthPx}>
        <div className="resume-preview-paged-host w-full flex flex-col items-center">
          {(lineGuides || fontSizeDebug) && (
            <ResumePreviewDebugLegend lineGuides={lineGuides} fontSizeDebug={fontSizeDebug} />
          )}

          <div
            className="resume-preview-paged-stack flex flex-col items-center"
            style={{ width: pageWidthPx > 0 ? pageWidthPx : '210mm' }}
            data-resume-page-guides="classic"
          >
        {sliceStarts.map((start, pageIndex) => {
          const leadPx = sliceLeads[pageIndex] ?? 0
          const sliceH = sliceHeightForPage(sliceStarts, pageIndex, bodyHeight)
          const visibleSliceH = Math.max(0, sliceH - leadPx)
          const clipH = Math.min(
            visibleSliceH > 0 ? visibleSliceH : contentZoneHeightPx,
            contentZoneHeightPx
          )
          return (
            <ClassicPageSlice
              key={start}
              start={start}
              leadPx={leadPx}
              clipH={clipH}
              pageWidthPx={pageWidthPx}
              pageHeightPx={pageHeightPx}
              pageFrameStyle={pageFrameStyle}
              lineGuides={lineGuides}
              fontSizeDebug={fontSizeDebug}
            >
              {children}
            </ClassicPageSlice>
          )
        })}
          </div>
        </div>
      </ResumePreviewPanelFit>
    </>
  )
}

function ClassicPageSlice({
  children,
  start,
  leadPx,
  clipH,
  pageWidthPx,
  pageHeightPx,
  pageFrameStyle,
  lineGuides,
  fontSizeDebug,
}: {
  children: React.ReactNode
  start: number
  leadPx: number
  clipH: number
  pageWidthPx: number
  pageHeightPx: number
  pageFrameStyle: React.CSSProperties
  lineGuides?: boolean
  fontSizeDebug?: boolean
}) {
  const clipRef = useRef<HTMLDivElement>(null)
  return (
    <div
      className="resume-preview-a4-sheet resume-preview-a4-sheet--clipped box-border relative shrink-0 overflow-hidden"
      style={{
        width: pageWidthPx > 0 ? pageWidthPx : '210mm',
        height: pageHeightPx > 0 ? pageHeightPx : '297mm',
      }}
    >
      <div className="resume-preview-page-frame" style={pageFrameStyle}>
        <div
          className={`resume-preview-page-clip relative${
            fontSizeDebug ? ' overflow-visible' : ' overflow-hidden'
          }`}
          style={{ height: clipH }}
        >
          <div ref={clipRef} className="relative h-full w-full overflow-hidden">
            <div
              className="resume-preview-page-slice-inner"
              style={{
                width: '100%',
                transform: `translateY(-${start + leadPx}px)`,
              }}
            >
              <ResumePreviewDebugContent lineGuides={lineGuides}>{children}</ResumePreviewDebugContent>
            </div>
          </div>
      {fontSizeDebug && (
        <ResumePreviewFontSizeLabelsOverlay
          enabled
          measureRootRef={clipRef}
          overlayHeightPx={clipH}
        />
      )}
      {lineGuides && <ResumePreviewPageBreakEdge />}
        </div>
      </div>
    </div>
  )
}
